import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'
import type { ErrorEvent } from '@sentry/vue'

/**
 * 配置可过滤的错误类型及匹配规则
 * 新增错误类型只需在这里添加即可
 */
const FILTERED_ERROR_TYPES: Record<string, RegExp[]> = {
  ResizeObserverLoopError: [/./], // 匹配任意值
  // NotFoundError: [/(GET|POST|PUT|DELETE|PATCH)\s+.*\s+404/],
  TooManyRequestsError: [/(GET|POST|PUT|DELETE|PATCH)\s+.*\s+429/],
  // ===== 发版后资源更新相关错误 =====
  Error: [
    /Unable to preload CSS/, // CSS 预加载失败
  ],
  TypeError: [
    /is not a valid JavaScript MIME type/, // JS 资源返回了 HTML（旧 chunk 404）
  ],
}

/**
 * 判断事件是否需要过滤
 */
function isFilteredError(event: ErrorEvent): boolean {
  const errors = event.exception?.values ?? []
  for (const error of errors) {
    const errorType = error.type
    if (!errorType) continue

    const patterns = FILTERED_ERROR_TYPES[errorType]
    if (patterns && patterns.some((re: RegExp) => re.test(error.value || ''))) {
      return true
    }
  }
  return false
}

/**
 * 初始化 Sentry
 * @param app Vue 应用实例
 * @param router Vue Router 实例
 */
export function initSentry(app: App, router: Router) {
  const ENV = import.meta.env.MODE

  Sentry.init({
    enabled: import.meta.env.VITE_ENABLE_SENTRY === 'true',
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration({ router })],
    tracesSampleRate: 0.05,
    environment: ENV,
    release: import.meta.env.VITE_GIT_COMMIT_HASH,
    beforeSend(event: ErrorEvent) {
      try {
        // 过滤配置的错误类型（所有环境都过滤）
        if (isFilteredError(event)) {
          if (ENV !== 'production') {
            console.debug('[Sentry] filtered event:', event)
          }
          return null
        }

        // 可扩展：这里可以加更多自定义过滤逻辑

        return event
      } catch (e) {
        console.error('[Sentry] beforeSend error:', e)
        return null // 防止 beforeSend 异常影响页面
      }
    },
  })
}

/**
 * 手动捕获错误
 * @param error 错误对象
 * @param context 错误上下文
 */
export function captureException(error: Error, context?: Record<string, unknown>) {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value as Record<string, unknown>)
      })
      Sentry.captureException(error)
    })
  } else {
    Sentry.captureException(error)
  }
}
